import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { GameManager } from "../../Managers/GameManager";
import { PickController } from "./PickController";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { Symbol } from "../Symbol";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { Spot } from "./Spot";
import { PlayList } from "../../Components/Sounds/PlayList";
import { Tweens } from "../../Components/Effects/Tweens";
import { BaseView } from "../Base/BaseView";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { ModuleManager } from "../../Managers/ModuleManager";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { WinAnimatorPick } from "../../CustomComponents/WinAnimatorPick";
import { IPickRoundSpot } from "neo-games/Engine/Communication/Game/Types/IPickRoundSpot";
import { Symbols } from "../Symbols";
import { WinAnimator } from "../WinAnimator";
import { BaseElement } from "../../Components/Elements/Graphics/BaseElement";
import { DataManager } from "../../Managers/DataManager";
import { GameData } from "../../Channels/Common/Datas/GameData";
import { AmbientBackground } from "../../Utils/AmbientBackground";

var PickRoundSpotStatus = Communication.Game.PickRoundSpotStatus;

@engineComponent(ComponentTypes.DisplayObject)
export class PickView extends ContainerElement {
    public static ActualValue: number = 0;
    public static ActualValueType: string = "";

    private _controller: PickController;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _background: SwitchElement;
    private _foreground: ContainerElement;
    private _spots: Symbols;
    private _bottomContainer: ContainerElement;
    private _topContainer: ContainerElement;
    private _transition: SwitchElement;
    private _winAnimator: WinAnimatorPick;
    private _logo: SwitchElement;
    private _winAnimation: SwitchElement;

    private _statusTimer: FrameTimerEventEmitter;
    private _status: string;

    private _pickMessageStart: HTMLElement;
    private _pickMessageWin: HTMLElement;
    private _pickMessageEnd: HTMLElement;

    constructor() {
        super();

        this._statusTimer = CreateFrameTimer("statusTimer");
        this._statusTimer.on(FrameTimerEvents.Complete, this.statusTimerComplete.bind(this));

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof WinAnimatorPick) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "BottomContainer" && this.childs[key] instanceof ContainerElement) {
                    this._bottomContainer = this.childs["BottomContainer"];
                }

                if (key === "TopContainer" && this.childs[key] instanceof ContainerElement) {
                    this._topContainer = this.childs["TopContainer"];
                }

                if (key === "Spots" && this.childs[key] instanceof Symbols) {
                    this._spots = this.childs[key];
                    this.removeChild(this._spots);
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (key === "Foreground" && this.childs[key] instanceof SwitchElement) {
                    this._foreground = this.childs[key];
                }

                if (key === "Transition" && this.childs[key] instanceof SwitchElement) {
                    this._transition = this.childs["Transition"];
                }

                if (key === "Logo" && this.childs[key] instanceof SwitchElement) {
                    this._logo = this.childs[key];
                }

                if (key === "WinAnimation" && this.childs[key] instanceof SwitchElement) {
                    this._winAnimation = this.childs[key];
                }
            }
        }

        this._pickMessageStart = document.getElementById("pickMessageStart");
        if (this._pickMessageStart == null) {
            if (DEBUG) {
                this.log("ERROR no pickMessageStart");
            }
        }

        this._pickMessageWin = document.getElementById("pickMessageWin");
        if (this._pickMessageWin == null) {
            if (DEBUG) {
                this.log("ERROR no pickMessageWin");
            }
        }
        this._pickMessageEnd = document.getElementById("pickMessageEnd");
        if (this._pickMessageEnd == null) {
            if (DEBUG) {
                this.log("ERROR no pickMessageStart");
            }
        }
    }

    initReceived(controller: PickController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._spots != null) {
            if (GameManager.pickResponse != null) {
                if (this._winAnimator != null) {
                    this._winAnimator.on(WinAnimator.FinishWinCalculation, this.finishWinCalculation.bind(this));
                    this._winAnimator.on(WinAnimatorPick.FinishWinAnimator, this.finishWinAnimator.bind(this));

                    if (this._winAnimation != null) {
                        this._winAnimator.addElementInstance({ WinAnimation: this._winAnimation });
                    }

                    this._winAnimator.initialize(this._spots);
                    if (ModuleManager.pickActive()) {
                        if (GameManager.pickResponse.round != null && GameManager.pickResponse.round.spots != null) {
                            this._winAnimator.showSpots(GameManager.pickResponse.round.spots);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no pick response");
                }
            }
        }
    }

    /**
     *
     */
    private toggleElements() {
        if (this._spots != null) {
            if (this._topContainer != null) {
                this._topContainer.alpha = 1;
            }

            if (this._bottomContainer != null) {
                this._bottomContainer.alpha = 1;
            }
        }
    }

    private statusTimerComplete(): void {
        switch (this._status) {
            case "updateCounters":
                this.updateCounters();
                break;
            case "selectUpdate":
                this.updateCountersValues();
                break;
            case "select":
                this.enabledSpots();
                this._controller.selectComplete();
                break;
        }
    }

    disableLogo(): void {
        if (this._logo != null) {
            this.removeChild(this._logo);
        }
    }

    swapChilds(src: BaseElement, childName: string, dest: any): void {
        if (dest.childs[childName] != null) {
            dest.addChildAt(src, dest.getChildIndex(dest.childs[childName]));
        }
    }

    initIntro(): void {
        this.swapChilds(BaseView.Logo, "SwapLogo", this);
        this.swapChilds(BaseView.Background, "SwapBackground", this);
        this.swapChilds(BaseView.Foreground, "SwapForeground", this);
        this.swapChilds(BaseView.Rooster, "SwapRooster", this);
        this.swapChilds(BaseView.Speaker, "SwapSpeaker", this);

        PlayList.loop("SND_BonusLoop");

        this.toggleElements();

        if (BaseView.Logo != null && BaseView.Logo instanceof SwitchElement) {
            BaseView.Logo.goToCase("BONUS");
        }

        if (BaseView.Background != null && BaseView.Background instanceof SwitchElement) {
            BaseView.Background.goToCase("PICK");
            AmbientBackground.changeAmbientBackground("pick", 0);
        }

        if (BaseView.Foreground != null && BaseView.Foreground instanceof SwitchElement) {
            BaseView.Foreground.goToCase("PICK");
        }

        if (BaseView.Speaker != null && BaseView.Speaker instanceof SwitchElement) {
            BaseView.Speaker.goToCase("INIT_INTRO");
        }

        if (
            GameManager.pickResponse != null &&
            GameManager.pickResponse.round != null &&
            GameManager.pickResponse.round.spots != null
        ) {
            this.visible = true;

            this.enabledSpots();

            this._winAnimator.showPickInitWins(GameManager.pickResponse.round);

            if (GameManager.pickResponse.round.remainingPicks > 0) {
                if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
                    BaseView.Rooster.goToCase("BONUS_LOOP_3");
                }

                if (
                    this._pickMessageStart != null &&
                    this._pickMessageStart.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._pickMessageStart.classList.remove(HtmlController.DisabledClass);
                }
            } else {
                if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
                    BaseView.Rooster.goToCase("BONUS_LOOP_3");
                }

                if (
                    this._pickMessageStart != null &&
                    !this._pickMessageStart.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._pickMessageStart.classList.add(HtmlController.DisabledClass);
                }
            }
        }

        if (GameManager.pickResponse != null) {
            if (GameManager.pickResponse.totalWin > 0) {
                if (
                    this._pickMessageEnd != null &&
                    this._pickMessageEnd.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._pickMessageEnd.classList.remove(HtmlController.DisabledClass);
                }
            }
        }

        this._winAnimator.idle();
    }

    collect(): void {}

    start(): void {}

    /**
     *
     * @param spotId
     */
    select(spotId: number): void {
        PlayList.stop("SND_BonusLoop");
        PlayList.loop("SND_BonusSelectloop");

        if (
            GameManager.pickResponse != null &&
            GameManager.pickResponse.round != null &&
            GameManager.pickResponse.round.spots != null
        ) {
            for (let key in GameManager.pickResponse.round.spots) {
                let id: number = GameManager.pickResponse.round.spots[key].id;
                let spotData: IPickRoundSpot = GameManager.pickResponse.round.spots[key];

                if (spotData.status != null) {
                    if (spotData.status.toString() == PickRoundSpotStatus.Enabled.toString()) {
                        if (id === spotId) {
                            if (BaseView.ReelLights != null) {
                                if (BaseView.ReelLights[id] != null) {
                                    BaseView.ReelLights[id].goToCase("SELECT");
                                }
                            }
                        } else {
                            if (BaseView.ReelLights != null) {
                                if (BaseView.ReelLights[id] != null) {
                                    BaseView.ReelLights[id].goToCase("UNSELECT");
                                }
                            }
                        }
                    }
                }
            }
        }

        if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
            BaseView.Rooster.goToCase("BONUS_LOOP_ELECTRIC");
        }

        if (BaseView.Speaker != null && BaseView.Speaker instanceof SwitchElement) {
            BaseView.Speaker.goToCase("LOOP");
        }
    }

    end(): void {}

    startReceived(): void {}

    selectReceived(): void {
        if (
            GameManager.pickResponse != null &&
            GameManager.pickResponse.round != null &&
            GameManager.pickResponse.round.spots != null
        ) {
            // Set spots to correspond case
            this._winAnimator.setSpotsData(GameManager.pickResponse.round.spots);

            // Show win/s
            this._winAnimator.showPickWins(GameManager.pickResponse.round);

            if (
                this._pickMessageStart != null &&
                !this._pickMessageStart.classList.contains(HtmlController.DisabledClass)
            ) {
                this._pickMessageStart.classList.add(HtmlController.DisabledClass);
            }
        }
    }

    showWin(): void {}

    updateCounters(): void {
        this._controller.updateCounters();
    }

    updateCountersValues(): void {
        this._controller.updateCountersValues();
    }

    endReceived(): void {}

    finishWinCalculation(): void {
        if (GameManager.pickResponse != null) {
            if (GameManager.pickResponse.totalWin > 0) {
                if (
                    this._pickMessageWin != null &&
                    this._pickMessageWin.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._pickMessageWin.classList.remove(HtmlController.DisabledClass);
                }
                if (this._winAnimator.currentAnimationIndex == 3) {
                    if (!this._pickMessageWin.classList.contains("popAnimation")) {
                        this._pickMessageWin.classList.add("popAnimation");
                    }
                }
            }
        }
    }

    finishWinAnimator(): void {
        this._controller.allWinsFinished();

        if (GameManager.pickResponse != null) {
            if (GameManager.pickResponse.totalWin > 0) {
                if (
                    this._pickMessageEnd != null &&
                    this._pickMessageEnd.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._pickMessageEnd.classList.remove(HtmlController.DisabledClass);
                }

                if (
                    this._pickMessageWin != null &&
                    !this._pickMessageWin.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._pickMessageWin.classList.add(HtmlController.DisabledClass);
                }

                if (this._pickMessageWin != null && this._pickMessageWin.classList.contains("popAnimation")) {
                    this._pickMessageWin.classList.remove("popAnimation");
                }
            }
        }
    }

    intro(long: boolean = false): void {
        this.swapChilds(BaseView.Logo, "SwapLogo", this);
        this.swapChilds(BaseView.Background, "SwapBackground", this);
        this.swapChilds(BaseView.Foreground, "SwapForeground", this);
        this.swapChilds(BaseView.Rooster, "SwapRooster", this);
        this.swapChilds(BaseView.Speaker, "SwapSpeaker", this);

        this.toggleElements();

        if (BaseView.Logo != null && BaseView.Logo instanceof SwitchElement) {
            BaseView.Logo.goToCase("BONUS");
        }

        if (BaseView.Background != null && BaseView.Background instanceof SwitchElement) {
            BaseView.Background.goToCase("BASE_TO_PICK");
            AmbientBackground.changeAmbientBackground("pick", 330);
        }

        if (BaseView.Foreground != null && BaseView.Foreground instanceof SwitchElement) {
            BaseView.Foreground.goToCase("BASE_TO_PICK");
        }

        if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
            BaseView.Rooster.goToCase("BONUS_INTRO");
        }

        if (BaseView.Speaker != null && BaseView.Speaker instanceof SwitchElement) {
            BaseView.Speaker.goToCase("INTRO");
        }

        if (
            GameManager.pickResponse != null &&
            GameManager.pickResponse.round != null &&
            GameManager.pickResponse.round.spots != null
        ) {
            this.visible = true;
            this._winAnimator.showSpots(GameManager.pickResponse.round.spots);

            if (
                this._pickMessageStart != null &&
                this._pickMessageStart.classList.contains(HtmlController.DisabledClass)
            ) {
                this._pickMessageStart.classList.remove(HtmlController.DisabledClass);
            }

            if (
                this._pickMessageEnd != null &&
                !this._pickMessageEnd.classList.contains(HtmlController.DisabledClass)
            ) {
                this._pickMessageEnd.classList.add(HtmlController.DisabledClass);
            }
        }
        this._winAnimator.idle();

        this._introDelay.start(120);
    }

    /**
     *
     */
    enabledSpots(): void {
        if (
            GameManager.pickResponse != null &&
            GameManager.pickResponse.round != null &&
            GameManager.pickResponse.round.spots != null
        ) {
            this._winAnimator.setSpotsData(GameManager.pickResponse.round.spots);
        }
    }

    introFinished(): void {
        this.enabledSpots();

        PlayList.loop("SND_BonusLoop");

        this._controller.introFinished();
    }

    outro(): void {
        PlayList.stop("SND_BonusLoop");
        PlayList.stop("SND_BonusSelectloop");
        PlayList.play("SND_BonusEnd");

        this._winAnimator.reset(false);

        if (this._pickMessageEnd != null && !this._pickMessageEnd.classList.contains(HtmlController.DisabledClass)) {
            this._pickMessageEnd.classList.add(HtmlController.DisabledClass);
        }

        if (this._transition != null) {
            this._transition.goToCase("START");
        }

        if (BaseView.ReelLights != null) {
            for (let i = 0; i < BaseView.ReelLights.length; i++) {
                BaseView.ReelLights[i].goToCase("EMPTY");
            }
        }

        if (BaseView.Background != null && BaseView.Background instanceof SwitchElement) {
            BaseView.Background.goToCase("PICK_TO_BASE");
            AmbientBackground.changeAmbientBackground("base", 830);
        }

        if (BaseView.Speaker != null && BaseView.Speaker instanceof SwitchElement) {
            BaseView.Speaker.goToCase("OUTRO");
        }

        if (BaseView.Rooster != null && BaseView.Rooster instanceof SwitchElement) {
            BaseView.Rooster.goToCase("THUMB_UP_01");
        }

        this._winAnimator.showOnlyBonusSymbols();

        this._outroDelay.start(60);
    }

    outroFinished(): void {
        this._outroDelay.stop();
        this.visible = false;
        this._controller.outroFinished();
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Pick/PickView.ts
 **/